import { Button, Dialog, Flex, Select, useToasts, Text, Spinner, MenuButton } from '@applyboard/crystal-ui';
import { EditOutlineIcon, DeleteOutlineIcon, MoreVertOutlineIcon } from '@applyboard/ui-icons';
import { useState, Fragment, useMemo } from 'react';
import { useErrorHandler } from '~/hooks';
import { Agency, useAgencies } from '~/pages/Applications/data/useAgencies';
import { useAppProcessing } from '../../context';
import { useChangeAgency } from '../../hooks/useChangeAgency';
import { LabelValue } from '~/components/uie-fix/LabelValue';

export function ChangeAgency() {
  const { application } = useAppProcessing();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAgencyAssignmentOpen, setIsAgencyAssignmentOpen] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState('');
  const [isAgencyRemovalOpen, setIsAgencyRemovalOpen] = useState(false);

  const { changeAgency, isPatchingAgency } = useChangeAgency(application.id);
  const { agencies, isFetchingAgencys, isLoadingAgencys } = useAgencies(
    { 'page[size]': 1000, 'page[number]': 1 },
    { enabled: isAgencyAssignmentOpen, keepPreviousData: false }
  );
  const isAgenciesLoading = isFetchingAgencys || isLoadingAgencys;
  const selectableAgencies = useMemo(
    () => agencies.filter((agency) => agency.id !== application.agency?.id),
    [agencies, application.agency?.id]
  );

  const toast = useToasts();
  const { apiError } = useErrorHandler();

  const onSave = async () => {
    try {
      const selectedAgencyDetails = agencies.find((agency) => agency.id === selectedAgency);
      if (!selectedAgencyDetails) {
        apiError();
        return;
      }
      await changeAgency({
        agencyExternalId: selectedAgencyDetails.id,
        agencyName: selectedAgencyDetails.name,
        institutionSupport: selectedAgencyDetails.institutionSupport,
      });
      toast.positive('Agency has been successfully changed.');
      setSelectedAgency('');
      setIsAgencyAssignmentOpen(false);
      setTimeout(() => {
        window.location.href = `/applications/${application.id}`;
      }, 1000);
    } catch (err) {
      apiError();
    }
  };

  const onRemove = async () => {
    try {
      await changeAgency({
        agencyExternalId: '',
        agencyName: '',
      });
      toast.positive('Agency has been removed.');
      setIsAgencyRemovalOpen(false);
      setTimeout(() => {
        window.location.href = `/applications/${application.id}`;
      }, 1000);
    } catch (err) {
      apiError();
    }
  };

  return (
    <>
      <Flex gap={2}>
        {application.agency ? (
          <MenuButton
            alignment="right"
            onOpenChange={(value) => {
              setIsMenuOpen(value);
            }}
            open={isMenuOpen}
          >
            <MenuButton.Trigger>
              <Button
                emphasis="transparent"
                aria-label="Edit application agency"
                leadIcon={MoreVertOutlineIcon}
                onClick={() => setIsMenuOpen(true)}
              />
            </MenuButton.Trigger>
            <MenuButton.Button
              icon={EditOutlineIcon}
              id="change-agency-assignment"
              onClick={() => setIsAgencyAssignmentOpen(true)}
            >
              Change application agency
            </MenuButton.Button>
            <MenuButton.Button icon={DeleteOutlineIcon} id="remove-agency" onClick={() => setIsAgencyRemovalOpen(true)}>
              Unassign agency
            </MenuButton.Button>
          </MenuButton>
        ) : (
          <Button
            intent="primary"
            emphasis="transparent"
            aria-label="Assign agency"
            onClick={() => setIsAgencyAssignmentOpen(true)}
            leadIcon={EditOutlineIcon}
          />
        )}
      </Flex>

      <Dialog
        open={isAgencyAssignmentOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setSelectedAgency('');
            setIsAgencyAssignmentOpen(false);
          }
        }}
      >
        <Dialog.Heading>Edit application agency</Dialog.Heading>
        <Dialog.Content>
          <Flex gap={6} direction="column">
            {application.agency?.agencyName && (
              <LabelValue label="Current agency" value={application.agency?.agencyName} />
            )}
            {isAgenciesLoading ? (
              <Flex align="center" gap={2}>
                <Spinner size="sm" />
                <Text variant="bodyM">Loading agencies...</Text>
              </Flex>
            ) : (
              <>
                <SelectAgency
                  agencies={selectableAgencies}
                  selectedAgency={selectedAgency}
                  setSelectedAgency={setSelectedAgency}
                />
                <Text variant="bodyS" contrast="low">
                  Once you assign an agency, all agents associated with this agency will see this application in their
                  portal.
                </Text>
              </>
            )}
          </Flex>
        </Dialog.Content>
        <Dialog.Actions>
          <Dialog.CloseButton width="fill">Cancel</Dialog.CloseButton>
          <Button
            intent="primary"
            width="fill"
            disabled={!selectedAgency || isPatchingAgency || isAgenciesLoading}
            onClick={() => {
              onSave();
            }}
            loading={isPatchingAgency}
          >
            Confirm
          </Button>
        </Dialog.Actions>
      </Dialog>

      <Dialog
        open={isAgencyRemovalOpen}
        onOpenChange={(isOpen) => {
          if (isPatchingAgency) return;
          if (!isOpen) setIsAgencyRemovalOpen(false);
        }}
      >
        <Dialog.Heading>Remove application agency</Dialog.Heading>
        <Dialog.Content>
          <Flex gap={4} direction="column">
            {application.agency?.agencyName && (
              <LabelValue label="Current agency" value={application.agency.agencyName} />
            )}
            <Text variant="bodyM">This will remove the agency access from this application.</Text>
          </Flex>
        </Dialog.Content>
        <Dialog.Actions>
          <Dialog.CloseButton width="fill" disabled={isPatchingAgency}>
            Cancel
          </Dialog.CloseButton>
          <Button
            intent="negative"
            width="fill"
            onClick={onRemove}
            loading={isPatchingAgency}
            disabled={isPatchingAgency}
          >
            Remove
          </Button>
        </Dialog.Actions>
      </Dialog>
    </>
  );
}

interface SelectAgencyProps {
  agencies: Agency[];
  selectedAgency: string;
  setSelectedAgency: React.Dispatch<React.SetStateAction<string>>;
}

const SelectAgency = ({ agencies, selectedAgency, setSelectedAgency }: SelectAgencyProps) => {
  return (
    <Select
      label="New agency"
      required
      onChange={(value) => setSelectedAgency(value)}
      appearance="styled"
      value={selectedAgency}
      aria-label="agency"
    >
      {agencies.length > 0
        ? agencies.map((program) => (
            <Fragment key={program.id}>
              <Select.Option label={program.name} value={program.id} />
            </Fragment>
          ))
        : null}
    </Select>
  );
};
